import React, { useState, useEffect } from 'react'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import api from '../api';
import Reordering from '../components/Reordering';
import { IoIosSend } from "react-icons/io";
import '../styles/select.css'
import { HiClipboardCheck, HiOutlineSearch, HiOutlineCreditCard, HiOutlinePencilAlt } from 'react-icons/hi';
import { Helmet } from 'react-helmet-async';
import { sendTelegramMessage, sendTelegramPhoto } from '../lib/telegram';
import { useTr } from '../lib/i18n';
import { Link } from 'react-router-dom';
import { ruUrl } from '../lib/ru';

const PROPERTY_RU = { 1: 'Квартира', 2: 'Дом', 3: 'Коммерческое помещение', 4: 'Участок', 10: 'Гараж', 11: 'Прочее' };

const Request = () => {
    const tr = useTr();
    const lang = tr('ro', 'ru');
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [locText, setLocText] = useState('');
    const [propertyType, setPropertyType] = useState(0);
    const [price, setPrice] = useState(0);
    const [condition, setCondition] = useState(0);
    const [note, setNote] = useState('');
    const [images, setImages] = useState([]);
    const [intention, setIntention] = useState('Vreau să vând');

    const [locations, setLocations] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [conditions, setConditions] = useState([]);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);

    const roUrl = 'https://lvgimob.md/cerere';
    const ruCanonical = ruUrl('/ru/cerere');

    const intentionOptions = lang === 'ru'
        ? ['Хочу продать', 'Сдаю в аренду']
        : ['Vreau să vând', 'Ofer la chirie'];

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [locationsRes, sectorsRes, propertyTypesRes, conditionsRes] = await Promise.all([
                    api.get('/api/v1/locations/'),
                    api.get('/api/v1/sectors/'),
                    api.get('/api/v1/property-types/'),
                    api.get('/api/v1/conditions/')
                ]);

                if (
                    locationsRes.status === 200 &&
                    sectorsRes.status === 200 &&
                    propertyTypesRes.status === 200 &&
                    conditionsRes.status === 200
                ) {
                    setLocations(locationsRes.data);
                    setSectors(sectorsRes.data);
                    setPropertyTypes(propertyTypesRes.data);
                    setConditions(conditionsRes.data);
                    setShow(true);
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                } else {
                    throw new Error('Eroare');
                }
            } catch (error) {
                toast.error(tr('Nu s-a putut încărca informația, încercați mai târziu.', 'Не удалось загрузить данные. Попробуйте позже.'));
                setShow(false);
            }
        };

        fetchAllData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);

        try {
            const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
            const noteWithMeta = [
                intention ? `[${intention}]` : '',
                locText ? `${tr('Locație', 'Локация')}: ${locText}` : '',
                note,
            ].filter(Boolean).join(' ');

            const res = await api.post('/api/v1/requests/', {
                first_name: nameParts[0] || '-',
                last_name: nameParts.slice(1).join(' ') || '-',
                phone: phone,
                email: 'cerere@lvgimob.md',
                location_id: 2,
                sector_id: null,
                property_type_id: propertyType,
                estimated_price: price,
                condition_id: condition,
                note: noteWithMeta
            })

            if (res.status === 201) {
                toast.success(tr('Cerere creată cu succes.', 'Заявка успешно создана.'))
                setFullName('');
                setPhone('');
                setLocText('');
                setNote('');
                setCondition(0);
                setPrice(0);
                setPropertyType(0);
                setErrors({});

                // Notify Telegram with the form data + uploaded images.
                const typeName = propertyTypes.find(p => p.id === propertyType)?.type || '-';
                const summary = lang === 'ru'
                    ? 'Новая заявка от собственника (RU)\n' +
                      `Намерение: ${intention}\n` +
                      `Имя: ${fullName}\n` +
                      `Телефон: ${phone}\n` +
                      `Локация объекта: ${locText || '-'}\n` +
                      `Тип недвижимости: ${PROPERTY_RU[propertyType] || typeName}\n` +
                      `Цена: ${price}€\n` +
                      `Примечания: ${note || '-'}`
                    : 'Cerere nouă de la un proprietar\n' +
                      `Intenția: ${intention}\n` +
                      `Nume: ${fullName}\n` +
                      `Telefon: ${phone}\n` +
                      `Locația imobilului: ${locText || '-'}\n` +
                      `Tip proprietate: ${typeName}\n` +
                      `Preț: ${price}€\n` +
                      `Note: ${note || '-'}`;
                sendTelegramMessage(summary);
                images.forEach((img, idx) => {
                    sendTelegramPhoto(img, tr(`Foto ${idx + 1} din ${images.length}`, `Фото ${idx + 1} из ${images.length}`));
                });
                setImages([]);
            } else {
                setErrors(res.data);
            }
        } catch (error) {
            if (error?.response?.data) {
                setErrors(error.response.data);
            } else {
                setErrors(prev => ({ ...prev, form: tr('Încercați mai târziu', 'Попробуйте позже') }));
            }
        } finally {
            setLoading(false);
        }
    }

    const steps = [
        {
            icon: <HiClipboardCheck className="text-white text-2xl" />,
            text: tr('1. Semnăm contractul Servicii de brokeraj imobiliar', '1. Подписываем договор на услуги брокериджа'),
        },
        {
            icon: <HiOutlineSearch className="text-white text-2xl" />,
            text: tr('2. Identificăm potențialii cumpărători', '2. Находим потенциальных покупателей'),
        },
        {
            icon: <HiOutlineCreditCard className="text-white text-2xl" />,
            text: tr('3. Stabilim detaliile procesului de vânzare', '3. Согласовываем детали процесса продажи'),
        },
        {
            icon: <HiOutlinePencilAlt className="text-white text-2xl" />,
            text: tr('4. Finalizăm tranzacția', '4. Завершаем сделку'),
        },
    ];

    return (
        <>
            <Helmet>
                <title>{tr('Vinde-ți proprietatea rapid | Cerere vânzare - LVG Imob', 'Продайте недвижимость быстро | Заявка на продажу - LVG Imob')}</title>
                <meta name="description" content={tr(
                    'Vrei să vinzi o proprietate? Trimite o cerere către LVG Imob și discută cu un consultant despre evaluare, promovare și găsirea cumpărătorului potrivit.',
                    'Хотите продать недвижимость? Отправьте заявку в LVG Imob и обсудите с консультантом оценку, продвижение и поиск подходящего покупателя.'
                )} />
                <link rel="canonical" href={lang === 'ru' ? ruCanonical : roUrl} />
                <link rel="alternate" hreflang="ro" href={roUrl} />
                <link rel="alternate" hreflang="ru" href={ruCanonical} />
                <link rel="alternate" hreflang="x-default" href={roUrl} />
            </Helmet>

            <ToastContainer position="bottom-left" autoClose={5000} />
            <section className='py-[60px] sm:py-[80px] px-[12px] flex flex-col md:flex-row gap-[40px] xl:gap-[80px] justify-between sm:container sm:mx-auto' style={{ minHeight: "calc(100vh - 88px - 561px)" }}>
                {
                    !show && (
                        <div className={`w-full h-full flex justify-center items-center pointer-events-none`}>
                            <Reordering />
                        </div>
                    )
                }

                {
                    show && (
                        <>
                            <div className="flex flex-col gap-[30px] w-full md:max-w-[60%]">
                                <div className="flex flex-col">
                                    <p className='font-poppins text-[#181616] text-[26px] xxs:text-[30px] font-[500]'>{tr('Completați cererea', 'Заполните заявку')}</p>
                                    <p className='font-body text-[#181616] tracking-wide text-[14px]'>{tr('Primiți un răspuns în cel mai scurt timp.', 'Вы получите ответ в кратчайшие сроки.')}</p>
                                </div>
                                <div className="relative min-h-[400px]">
                                    <form
                                        onSubmit={handleSubmit}
                                        className={`flex flex-col gap-[10px] ${loading ? 'opacity-0' : 'opacity-100'}`}
                                    >
                                        <div className="flex flex-col gap-[5px]">
                                            <p className='text-[14px] font-poppins font-[500]'>{tr('Nume Prenume', 'Имя Фамилия')}</p>
                                            <input type="text" name="nume" id="nume"
                                                className='font-body px-[24px] py-[17px] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px]'
                                                placeholder={tr('Nume Prenume', 'Имя Фамилия')}
                                                value={fullName}
                                                onChange={(e) => { setFullName(e.target.value); const { first_name, last_name, ...rest } = errors; setErrors(rest); }} />
                                            <p className={`text-[14px] text-red-400 transition-opacity duration-150 font-body ease-in ${(errors?.first_name || errors?.last_name) ? 'opacity-100' : 'opacity-0'}`}>{errors.first_name || errors.last_name || '.'}</p>
                                        </div>

                                        <div className="flex flex-col gap-[5px]">
                                            <p className='text-[14px] font-poppins font-[500]'>{tr('Intenția', 'Намерение')}</p>
                                            <div className="flex gap-[10px]">
                                                {intentionOptions.map((option) => (
                                                    <label key={option} className={`flex-1 flex items-center justify-center gap-[8px] border-[1px] rounded-[5px] px-[16px] py-[12px] text-[15px] font-body cursor-pointer transition-all duration-100 ease-in ${intention === option ? 'border-[#d5ab63] bg-[#fdf6ea]' : 'border-gray-300 hover:bg-gray-100'}`}>
                                                        <input
                                                            type="radio"
                                                            name="intention"
                                                            value={option}
                                                            checked={intention === option}
                                                            onChange={(e) => setIntention(e.target.value)}
                                                            className="accent-[#d5ab63]"
                                                        />
                                                        {option}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-[5px]">
                                            <p className='text-[14px] font-poppins font-[500]'>{tr('Număr de telefon', 'Номер телефона')}</p>
                                                <input type="tel" name="phone" id="phone" 
                                                    inputMode="numeric"
                                                    pattern="^\+?\d{0,15}$"
                                                    className='font-body px-[24px] py-[17px] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px] num'
                                                    placeholder={tr('Număr de telefon', 'Номер телефона')}
                                                    value={phone}
                                                    onChange={(e) => {
                                                        const input = e.target.value;
                                                        const formatted = input.replace(/(?!^\+)[^\d]/g, '');
                                                        setPhone(formatted);
                                                        const { phone, ...rest } = errors;
                                                        setErrors(rest);
                                                    }} 
                                                />
                                                <p className={`text-[14px] text-red-400 transition-opacity duration-150 font-body ease-in ${errors?.phone ? 'opacity-100' : 'opacity-0'}`}>{errors.phone || '.'}</p>
                                        </div>

                                        <div className="flex flex-col lg:flex-row lg:gap-[20px]">
                                            <div className="flex flex-col gap-[5px] w-full w-1/2">
                                                <p className='text-[14px] font-poppins font-[500]'>{tr('Tipul proprietății', 'Тип недвижимости')}</p>
                        <select name="propertyType" id="propertyType"
                                                    className='font-body px-[24px] py-[17px] text-[#181616] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px]'
                                                    value={propertyType || ''}
                                                    onChange={(e) => { setPropertyType(e.target.value); const { property_type_id, ...rest } = errors; setErrors(rest); }}>
                                                    <option value="" disabled>{tr('Tipul proprietății', 'Тип недвижимости')}</option>
                                                    {propertyTypes.map((type) => (
                                                        <option key={type.id} value={type.id}>
                                                            {lang === 'ru' ? (PROPERTY_RU[type.id] || type.type) : type.type}
                                                        </option>
                                                    ))}
                                                </select>
                                                <p className={`text-[14px] text-red-400 transition-opacity duration-150 font-body ease-in ${errors?.property_type_id ? 'opacity-100' : 'opacity-0'}`}>{errors.property_type_id || '.'}</p>
                                            </div>
                                            <div className="flex flex-col gap-[5px] w-full w-1/2">
                                                <p className='text-[14px] font-poppins font-[500]'>{tr('Locația imobilului', 'Локация объекта')}</p>
                                                <input
                                                    type="text"
                                                    name="location"
                                                    id="location"
                                                    className='font-body px-[24px] py-[17px] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px]'
                                                    placeholder={tr('În ce locație se află imobilul', 'Где находится объект')}
                                                    value={locText}
                                                    onChange={(e) => setLocText(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex flex-col lg:flex-row lg:gap-[20px]">
                                            <div className="flex flex-col gap-[5px] w-full w-1/2">
                                                <p className='text-[14px] font-poppins font-[500]'>{tr('Condiție', 'Состояние')}</p>
                                                <select name="condition" id="condition"
                                                    className='font-body px-[24px] py-[17px] text-[#181616] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px]'
                                                    value={condition || ''}
                                                    onChange={(e) => { setCondition(e.target.value); const { condition_id, ...rest } = errors; setErrors(rest); }}>
                                                    <option value="" disabled>{tr('Condiție', 'Состояние')}</option>
                                                    {conditions.map((con) => (
                                                        <option key={con.id} value={con.id}>
                                                            {con.condition}
                                                        </option>
                                                    ))}
                                                </select>
                                                <p className={`text-[14px] text-red-400 transition-opacity duration-150 font-body ease-in ${errors?.condition_id ? 'opacity-100' : 'opacity-0'}`}>{errors.condition_id || '.'}</p>
                                            </div>
                                            <div className="flex flex-col gap-[5px] w-full w-1/2">
                                                <p className='text-[14px] font-poppins font-[500]'>{tr('Preț', 'Цена')}</p>
                                                <div className="font-body flex gap-[5px] px-[24px] py-[17px] rounded-[5px] border-[1px] border-gray-300 text-[17px]">
                                                    <p>€</p>
                                                    <input
                                                    type="text"
                                                    name="price"
                                                    id="price"
                                                    inputMode="numeric"
                                                    pattern="\d*"
                                                    className='outline-none w-full'
                                                    placeholder={tr('Preț', 'Цена')}
                                                    value={price}
                                                    onChange={(e) => {
                                                        let input = e.target.value.replace(/[^\d]/g, '');

                                                        if (input.length > 1 && input.startsWith('0')) {
                                                            input = input.replace(/^0+/, '');
                                                        }

                                                        if (input === '') {
                                                            setPrice(0);
                                                        } else {
                                                            setPrice(input);
                                                        }

                                                        const { estimated_price, ...rest } = errors;
                                                        setErrors(rest);
                                                    }}
                                                    />
                                                </div>
                                                <p className={`text-[14px] text-red-400 transition-opacity duration-150 font-body ease-in ${errors?.estimated_price ? 'opacity-100' : 'opacity-0'}`}>{errors.estimated_price || '.'}</p>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-[5px]">
                                            <p className='text-[14px] font-poppins font-[500]'>{tr('Imagini ale proprietății (opțional)', 'Фотографии объекта (необязательно)')}</p>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={(e) => setImages(Array.from(e.target.files || []))}
                                                className='font-body text-[14px] py-[12px] px-[24px] rounded-[5px] outline-none border-[1px] border-gray-300 bg-white cursor-pointer'
                                            />
                                            {images.length > 0 && (
                                                <div className="flex flex-wrap gap-[10px] mt-[5px]">
                                                    {images.map((file, idx) => (
                                                        <img
                                                            key={idx}
                                                            src={URL.createObjectURL(file)}
                                                            alt=""
                                                            className="w-[70px] h-[70px] object-cover rounded-[5px]"
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-[5px]">
                                            <p className='text-[14px]  font-poppins font-[500]'>{tr('Note', 'Примечания')}</p>
                                            <textarea name="note" id="note"
                                                className='font-body px-[24px] py-[17px] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px] min-h-[90px]'
                                                placeholder={tr('Note', 'Примечания')}
                                                value={note}
                                                onChange={(e) => { setNote(e.target.value); const { note, ...rest } = errors; setErrors(rest); }}></textarea>
                                            <p className={`text-[14px] text-red-400 transition-opacity duration-150 font-body ease-in ${errors?.note ? 'opacity-100' : 'opacity-0'}`}>{errors.note || '.'}</p>
                                        </div>
                                        <p className="text-[12.5px] font-body text-gray-500 leading-[1.55]">
                                            {lang === 'ru'
                                                ? <>Заполняя форму и нажимая кнопку отправки, вы подтверждаете, что ознакомились с <Link to="/ru/usloviya-ispolzovaniya" className="underline hover:text-[#b48a4d]">Условиями использования</Link> и <Link to="/ru/politika-konfidentsialnosti" className="underline hover:text-[#b48a4d]">Политикой конфиденциальности</Link>, и соглашаетесь на получение ответа по отправленному запросу.</>
                                                : <>Prin completarea formularului și apăsarea butonului de trimitere, confirmați că ați citit <Link to="/termeni-si-conditii" className="underline hover:text-[#b48a4d]">Termenii și condițiile</Link> și <Link to="/politica-de-confidentialitate" className="underline hover:text-[#b48a4d]">Politica de confidențialitate</Link> și sunteți de acord să fiți contactat în legătură cu solicitarea transmisă.</>
                                            }
                                        </p>
                                        <button type="submit" className='py-[20px] rounded-[5px] font-[500] cursor-pointer uppercase text-white text-[15px] font-body flex items-center justify-center gap-[10px] transition-all duration-150 ease-in btn btn-main'>
                                            {tr('Trimite', 'Отправить')}
                                            <IoIosSend className='text-2xl' />
                                        </button>
                                        <p className={`text-[14px] text-red-400 text-center mt-[10px] transition-opacity font-body duration-150 ease-in ${errors?.form ? 'opacity-100' : 'opacity-0'}`}>{errors.form || '.'}</p>
                                    </form>

                                    <div className={`absolute top-0 left-0 w-full h-full flex justify-center items-center ${loading ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                        <Reordering />
                                    </div>
                                </div>

                            </div>

                            <div className="max-w-md mx-auto space-y-[8px] md:mt-[120px]">
                                {steps.map(({ icon, text }, idx) => (
                                    <div key={idx} className="flex items-start gap-4">
                                        <div className="relative flex flex-col items-center">
                                            <div
                                                className="flex items-center justify-center rounded-[5px] p-[11px]"
                                                style={{ backgroundColor: '#d5ab63' }}
                                            >
                                                {React.cloneElement(icon, { className: 'text-white text-3xl' })}
                                            </div>

                                            {idx !== steps.length - 1 && (
                                                <span
                                                    className="block w-[2px] bg-[#d5ab63] flex-grow mt-2"
                                                    style={{ minHeight: '40px' }}
                                                />
                                            )}
                                        </div>

                                        <p className="text-[#181616] font-poppins text-[16px] cstmv:text-[18px] font-[500] tracking-wide">{text}</p>
                                    </div>
                                ))}
                            </div>

                        </>
                    )
                }
            </section>
        </>
    )
}

export default Request
